import { setAppStatus } from '../shell/app-status/appStatus.js';

/**
 * What happens when the session can no longer be refreshed (refresh token revoked, account disabled, password changed,
 * the per-tab Firebase session gone). The page is NOT taken away: the sign-in dialog (SessionExpiredDialog) opens over
 * it, requests that need the session wait for it, and once the same user signs in again they are sent - so a form the
 * user was filling in survives. Signing in as someone else, or choosing the sign-in page, ends the session cleanly and
 * goes to sign-in with a link back to this page.
 *
 * A network failure while refreshing is not an expired session: the user stays signed in and the offline / server
 * status says why requests fail (appStatus.js).
 */

let pending = null; // { promise, resolve, reject, uid }
let deliberateSignOut = false;

const LOGIN_PATH = '/account';

export const isReauthPending = () => !!pending;

/**
 * Ask the user to sign in again. Resolves when they have; rejects when they leave for the sign-in page or sign in as
 * someone else. Every caller while the dialog is up shares the one promise.
 */
export const requestReauth = (uid) => {
    if (!pending) {
        let resolve;
        let reject;
        const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
        // Nobody may be waiting (the dialog opened from a Firebase sign-out); don't report that as unhandled.
        promise.catch(() => {});
        pending = { promise, resolve, reject, uid: uid || null };
        setAppStatus({ sessionExpired: true });
    } else if (!pending.uid && uid) {
        pending.uid = uid;
    }
    return pending.promise;
};

/** Requests made while the dialog is up wait for it instead of failing one by one. */
export const waitForReauth = () => (pending ? pending.promise : Promise.resolve());

/** The uid the dialog expects back (null when not known). */
export const expectedReauthUid = () => pending?.uid || null;

/**
 * The user signed in again. Returns false (and ends the wait with an error) when it is a different account: this
 * tab's state belongs to the old one, so the caller must clear it.
 */
export const completeReauth = (uid) => {
    if (!pending) return true;
    const p = pending;
    pending = null;
    setAppStatus({ sessionExpired: false });
    if (p.uid && uid !== p.uid) {
        p.reject(new Error('reauth-different-user'));
        return false;
    }
    p.resolve();
    return true;
};

/** The user chose the sign-in page instead: waiting requests fail and the dialog closes. */
export const abandonReauth = () => {
    if (!pending) return;
    const p = pending;
    pending = null;
    setAppStatus({ sessionExpired: false });
    p.reject(new Error('reauth-abandoned'));
};

/** Logout and the session cleanup sign out on purpose; a Firebase sign-out after this is not an expired session. */
export const markDeliberateSignOut = () => { deliberateSignOut = true; };
export const clearDeliberateSignOut = () => { deliberateSignOut = false; };
export const isDeliberateSignOut = () => deliberateSignOut;

/** Firebase could not reach its token server - a connection problem, not an ended session. */
export const isNetworkAuthError = (err) => err?.code === 'auth/network-request-failed'
    || (typeof navigator !== 'undefined' && navigator.onLine === false);

/**
 * A path to come back to after sign-in, or null. Only a path inside this app: starts with one "/", is not
 * protocol-relative ("//evil.example") or a backslash trick, and is not the sign-in page itself.
 */
export const safeReturnTo = (raw) => {
    if (typeof raw !== 'string' || !raw) return null;
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
    if (/[\u0000-\u001f]/.test(raw)) return null;
    try {
        const url = new URL(raw, 'https://geoplay.invalid');
        if (url.origin !== 'https://geoplay.invalid') return null;
        if (url.pathname === LOGIN_PATH || url.pathname.startsWith(`${LOGIN_PATH}/`)) return null;
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
};

const currentPath = () => {
    if (typeof window === 'undefined') return '/';
    const { pathname, search, hash } = window.location;
    return `${pathname}${search}${hash}`;
};

/** The sign-in page, with a link back to `returnTo` (default: where the user is now). */
export const loginUrl = (returnTo = currentPath()) => {
    const back = safeReturnTo(returnTo);
    return back ? `${LOGIN_PATH}?mode=login&returnTo=${encodeURIComponent(back)}` : `${LOGIN_PATH}?mode=login`;
};

/** Test hook: forget any pending sign-in. */
export const __resetSessionExpiry = () => {
    pending = null;
    deliberateSignOut = false;
};
